const API_BASE_URL = process.env.VUE_APP_API_URL || 'https://tk64exs68l.execute-api.ap-southeast-2.amazonaws.com'

// Wait (up to maxMs) for the Auth0 SDK to finish its initial loading so that
// isAuthenticated reflects the real state. Without this, on first login the
// view can call api.* while isLoading is still true and isAuthenticated is
// transiently false, producing a tokenless request that the Lambda rejects
// with "Missing bearer token".
const waitForAuth0Ready = async (auth0, maxMs = 5000) => {
  if (!auth0?.isLoading) return
  const start = Date.now()
  while (auth0.isLoading.value && Date.now() - start < maxMs) {
    await new Promise((resolve) => setTimeout(resolve, 50))
  }
}

// Helper function to get auth token - accepts auth0 instance
const getAuthToken = async (auth0) => {
  console.log('🔑 Getting auth token...', {
    auth0Available: !!auth0,
    isAuthenticated: auth0?.isAuthenticated?.value,
    timestamp: new Date().toISOString()
  })

  if (!auth0) {
    console.warn('⚠️ Auth0 instance not provided to getAuthToken')
    return null
  }

  try {
    await waitForAuth0Ready(auth0)

    if (!auth0.isAuthenticated.value) {
      console.warn('⚠️ User not authenticated after Auth0 ready', {
        isAuthenticated: auth0.isAuthenticated.value,
        isLoading: auth0.isLoading?.value
      })
      return null
    }

    const token = await auth0.getAccessTokenSilently()
    console.log('✅ Auth token retrieved successfully', {
      tokenLength: token?.length,
      hasToken: !!token
    })
    return token
  } catch (error) {
    console.error('❌ Unable to get access token:', {
      error: error.message,
      errorType: error.constructor.name,
      timestamp: new Date().toISOString()
    })
    return null
  }
}

// Generic API request function using fetch - accepts auth0 instance
const apiRequest = async (endpoint, options = {}, auth0 = null) => {
  const requestId = Math.random().toString(36).substring(7)
  const startTime = Date.now()
  
  console.log(`🌐 API Request [${requestId}] Starting:`, {
    endpoint,
    method: options.method || 'GET',
    hasAuth: !!auth0,
    timestamp: new Date().toISOString()
  })
  
  const token = await getAuthToken(auth0)
  
  const config = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  }
  
  const url = `${API_BASE_URL}${endpoint}`
  
  console.log(`📤 API Request [${requestId}] Config:`, {
    url,
    method: config.method,
    hasToken: !!token,
    headers: Object.keys(config.headers)
  })
  
  try {
    const response = await fetch(url, config)
    const duration = Date.now() - startTime
    
    console.log(`📥 API Response [${requestId}]:`, {
      status: response.status,
      statusText: response.statusText,
      duration: `${duration}ms`,
      headers: Object.fromEntries(response.headers.entries())
    })
    
    if (!response.ok) {
      if (response.status === 401) {
        console.warn(`🔄 API Request [${requestId}] - 401 Unauthorized, attempting token refresh...`)
        // Try to refresh token and retry once. Pass the auth0 instance through;
        // the previous version called getAuthToken() with no args, which always
        // returned null and broke the retry path silently.
        const refreshedToken = await getAuthToken(auth0)
        if (refreshedToken) {
          config.headers.Authorization = `Bearer ${refreshedToken}`
          console.log(`🔄 API Request [${requestId}] - Retrying with refreshed token...`)
          const retryResponse = await fetch(url, config)
          const retryDuration = Date.now() - startTime
          
          console.log(`📥 API Retry Response [${requestId}]:`, {
            status: retryResponse.status,
            statusText: retryResponse.statusText,
            totalDuration: `${retryDuration}ms`
          })
          
          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`)
          }
          const retryData = await retryResponse.json()
          console.log(`✅ API Request [${requestId}] - Retry successful`)
          return retryData
        } else {
          console.error(`❌ API Request [${requestId}] - Token refresh failed, redirecting to login`)
          window.location.href = '/login'
          throw new Error('Authentication failed')
        }
      }
      const errorText = await response.text()
      console.error(`❌ API Request [${requestId}] - HTTP Error:`, {
        status: response.status,
        statusText: response.statusText,
        errorText,
        duration: `${duration}ms`
      })
      const apiError = new Error(`HTTP error! status: ${response.status}`)
      apiError.status = response.status
      try { apiError.responseBody = JSON.parse(errorText) } catch (_) { apiError.responseBody = null }
      throw apiError
    }
    
    const data = await response.json()
    console.log(`✅ API Request [${requestId}] - Success:`, {
      dataKeys: Object.keys(data),
      duration: `${duration}ms`
    })
    return data
  } catch (error) {
    const duration = Date.now() - startTime
    console.error(`❌ API Request [${requestId}] - Failed:`, {
      error: error.message,
      errorType: error.constructor.name,
      duration: `${duration}ms`,
      endpoint,
      method: options.method || 'GET'
    })
    throw error
  }
}

// Factory function to create API service with auth0 instance
const createApiService = (auth0) => {
  const api = {
    get: (endpoint) => apiRequest(endpoint, {}, auth0),
    post: (endpoint, data) => apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    }, auth0),
    put: (endpoint, data) => apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    }, auth0),
    patch: (endpoint, data) => apiRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }, auth0),
    delete: (endpoint) => apiRequest(endpoint, {
      method: 'DELETE'
    }, auth0)
  }

  return api
}

// Legacy api object for compatibility (without auth)
const api = {
  get: (endpoint) => apiRequest(endpoint),
  post: (endpoint, data) => apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  put: (endpoint, data) => apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (endpoint) => apiRequest(endpoint, {
    method: 'DELETE'
  })
}

// Factory function to create API service with auth0
const createApiServiceWithAuth = (auth0) => {
  const authenticatedApi = createApiService(auth0)
  
  return {
  // Raw API methods for composables/generic use
  get: (endpoint) => authenticatedApi.get(endpoint),
  post: (endpoint, data) => authenticatedApi.post(endpoint, data),
  put: (endpoint, data) => authenticatedApi.put(endpoint, data),
  patch: (endpoint, data) => authenticatedApi.patch(endpoint, data),
  delete: (endpoint) => authenticatedApi.delete(endpoint),

  /**
   * Get technician assignments (bookings) for a given email and date range
   * @param {string} email - Technician's email address
   * @param {string} startDate - Start date in YYYY-MM-DD format (optional)
   * @param {string} endDate - End date in YYYY-MM-DD format (optional)
   * @param {string} status - Booking status filter (optional)
   * @returns {Promise} API response containing assignments
   */
  async getTechnicianAssignments(email, startDate = null, endDate = null, status = null) {
    console.log('📋 Getting technician assignments:', {
      hasEmail: !!email,
      startDate,
      endDate,
      status,
      timestamp: new Date().toISOString()
    })
    
    const params = new URLSearchParams({ email })
    
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (status) params.append('status', status)
    
    try {
      const response = await authenticatedApi.get(`/technician-assignments?${params.toString()}`)
      console.log('✅ Technician assignments retrieved:', {
        assignmentCount: response?.assignments?.length || 0,
        hasEmail: !!email,
        dateRange: `${startDate} to ${endDate}`
      })
      return response // api.get now returns the parsed JSON directly
    } catch (error) {
      console.error('❌ Error fetching technician assignments:', {
        error: error.message,
        hasEmail: !!email,
        startDate,
        endDate,
        status
      })
      throw error
    }
  },

  /**
   * Get fleet tasks visible to this technician within a date range.
   */
  async getTechnicianTasks(email, startDate = null, endDate = null) {
    const params = new URLSearchParams({ email })
    if (startDate) params.append('startDate', startDate)
    if (endDate) params.append('endDate', endDate)
    try {
      const response = await authenticatedApi.get(`/technician-tasks?${params.toString()}`)
      return response
    } catch (error) {
      console.error('Error fetching technician tasks:', error.message)
      throw error
    }
  },

  async getTask(taskId, email) {
    try {
      const params = new URLSearchParams({ email: email || '' })
      const response = await authenticatedApi.get(`/technician-tasks/${encodeURIComponent(taskId)}?${params.toString()}`)
      return response
    } catch (error) {
      console.error('Error fetching task:', error.message)
      throw error
    }
  },

  async updateTask(taskId, payload, email) {
    try {
      const params = new URLSearchParams({ email: email || '' })
      const response = await authenticatedApi.patch(`/technician-tasks/${encodeURIComponent(taskId)}?${params.toString()}`, payload)
      return response
    } catch (error) {
      console.error('Error updating task:', error.message)
      throw error
    }
  },

  /**
   * Get booking details by booking ID
   * @param {string} bookingId - Booking ID or booking reference
   * @returns {Promise} API response containing booking details
   */
  async getBooking(bookingId) {
    console.log('📖 Getting booking details:', {
      bookingId,
      timestamp: new Date().toISOString()
    })
    
    try {
      const response = await authenticatedApi.get(`/booking/${encodeURIComponent(bookingId)}`)
      console.log('✅ Booking details retrieved:', {
        bookingId,
        customerName: response?.customer_name,
        status: response?.booking_status,
        serviceDate: response?.service_date
      })
      return response
    } catch (error) {
      console.error('❌ Error fetching booking details:', {
        error: error.message,
        bookingId
      })
      throw error
    }
  },

  /**
   * Get technician profile by email
   * @param {string} email - Technician email
   * @returns {Promise} API response containing technician profile
   */
  async getTechnicianProfile(email) {
    try {
      const response = await authenticatedApi.get(`/technician-profile?email=${encodeURIComponent(email)}`)
      return response
    } catch (error) {
      console.error('Error fetching technician profile:', error)
      throw error
    }
  },

  /**
   * Update job status for a booking
   * @param {string} bookingReference - Booking reference (e.g., RSS-DUV4623000)
   * @param {string} formBookingId - Form booking ID (can be null)
   * @param {string} status - New status (en_route, arrived, in_progress, completed, cancelled)
   * @param {string} notes - Technician notes
   * @param {number} technicianId - Technician ID
   * @returns {Promise} API response containing update result
   */
  async updateJobStatus(bookingReference, formBookingId, status, notes = '', technicianId) {
    console.log('🔄 Updating job status:', {
      bookingReference,
      formBookingId,
      status,
      notesLength: notes?.length || 0,
      technicianId,
      timestamp: new Date().toISOString()
    })
    
    try {
      const payload = {
        booking_reference: bookingReference,
        status: status,
        technician_notes: notes,
        technician_id: technicianId
      }
      
      // Only include form_booking_id if it's not null/undefined
      if (formBookingId) {
        payload.form_booking_id = formBookingId
      }
      
      console.log('📤 Job status update payload:', payload)
      
      const response = await authenticatedApi.post('/bookings/update-job-status', payload)
      console.log('✅ Job status updated successfully:', {
        bookingReference,
        newStatus: status,
        response: response
      })
      return response
    } catch (error) {
      console.error('❌ Error updating job status:', {
        error: error.message,
        bookingReference,
        status,
        technicianId
      })
      throw error
    }
  },

  /**
   * Get job status history for a booking
   * @param {string} bookingReference - Booking reference (e.g., RSS-DUV4623000)
   * @param {string} formBookingId - Form booking ID (can be null)
   * @returns {Promise} API response containing status history
   */
  async getJobStatus(bookingReference, formBookingId = null) {
    try {
      const params = new URLSearchParams()
      
      if (bookingReference) {
        params.append('booking_reference', bookingReference)
      }
      
      if (formBookingId) {
        params.append('form_booking_id', formBookingId)
      }
      
      const response = await authenticatedApi.get(`/bookings/get-job-status?${params.toString()}`)
      return response
    } catch (error) {
      console.error('Error fetching job status:', error)
      throw error
    }
  },

  /**
   * Create QR code payment for technician field collection
   * @param {Object} paymentData - Payment details
   * @param {number} paymentData.amount - Amount in cents
   * @param {string} paymentData.description - Payment description
   * @param {Object} paymentData.metadata - Additional metadata
   * @returns {Promise} API response containing QR code data and payment intent
   */
  async createQRPayment(paymentData) {
    console.log('💳 Creating QR payment:', {
      amount: paymentData.amount,
      description: paymentData.description,
      metadataKeys: Object.keys(paymentData.metadata || {}),
      timestamp: new Date().toISOString()
    })
    
    try {
      const response = await authenticatedApi.post('/payments/qr-create', paymentData)
      console.log('✅ QR payment created successfully:', {
        paymentIntentId: response?.payment_intent_id,
        amount: paymentData.amount,
        qrCodeGenerated: !!response?.qr_code_data_url
      })
      return response
    } catch (error) {
      console.error('❌ Error creating QR payment:', {
        error: error.message,
        amount: paymentData.amount,
        description: paymentData.description
      })
      throw error
    }
  },

  /**
   * Check status of QR code payment
   * @param {string} paymentIntentId - Stripe payment intent ID
   * @returns {Promise} API response containing payment status and details
   */
  async checkQRPaymentStatus(paymentIntentId) {
    try {
      const response = await authenticatedApi.get(`/payments/qr-status/${encodeURIComponent(paymentIntentId)}`)
      return response
    } catch (error) {
      console.error('Error checking QR payment status:', error)
      throw error
    }
  },

  /**
   * Get all payment records for a booking
   * @param {string} bookingReference - Booking reference ID
   * @param {string} formBookingId - Optional form booking ID for additional filtering
   * @returns {Promise} API response containing all payment records for the booking
   */
  async getBookingPayments(bookingReference, formBookingId = null) {
    try {
      const params = new URLSearchParams()
      if (formBookingId) {
        params.append('formBookingId', formBookingId)
      }

      const queryString = params.toString() ? `?${params.toString()}` : ''
      const response = await authenticatedApi.get(`/bookings/${encodeURIComponent(bookingReference)}/payments${queryString}`)
      return response
    } catch (error) {
      console.error('Error fetching booking payments:', error)
      throw error
    }
  },

  // ===== TIMESHEET API METHODS =====

  /**
   * Submit weekly timesheet
   * @param {Object} timesheetData - Timesheet submission data
   * @returns {Promise} API response
   */
  async submitTimesheet(timesheetData) {
    console.log('📋 Submitting timesheet:', {
      technicianId: timesheetData.technician_id,
      weekStart: timesheetData.week_start_date,
      daysWorked: timesheetData.days_worked?.length,
      timestamp: new Date().toISOString()
    })

    try {
      const response = await authenticatedApi.post('/timesheets', timesheetData)
      console.log('✅ Timesheet submitted:', {
        success: response?.success,
        timesheetUuid: response?.timesheet_uuid,
        isUpdate: response?.is_update
      })
      return response
    } catch (error) {
      console.error('❌ Error submitting timesheet:', {
        error: error.message,
        technicianId: timesheetData.technician_id
      })
      throw error
    }
  },

  /**
   * Get technician timesheets
   * @param {number} technicianId - Technician ID
   * @param {number} weeks - Number of weeks to fetch (default 2)
   * @returns {Promise} API response with timesheets
   */
  async getTimesheets(technicianId, weeks = 2) {
    console.log('📋 Fetching timesheets:', {
      technicianId,
      weeks,
      timestamp: new Date().toISOString()
    })

    try {
      const params = new URLSearchParams({
        technician_id: technicianId,
        weeks: weeks
      })
      const response = await authenticatedApi.get(`/timesheets?${params.toString()}`)
      console.log('✅ Timesheets retrieved:', {
        count: response?.timesheets?.length || 0,
        technicianId
      })
      return response
    } catch (error) {
      console.error('❌ Error fetching timesheets:', {
        error: error.message,
        technicianId
      })
      throw error
    }
  },

  /**
   * Update existing timesheet
   * @param {number} timesheetId - Timesheet ID
   * @param {Object} timesheetData - Updated timesheet data
   * @returns {Promise} API response
   */
  async updateTimesheet(timesheetId, timesheetData) {
    console.log('📝 Updating timesheet:', {
      timesheetId,
      timestamp: new Date().toISOString()
    })

    try {
      const response = await authenticatedApi.put(`/timesheets/${timesheetId}`, timesheetData)
      console.log('✅ Timesheet updated:', {
        timesheetId,
        success: response?.success
      })
      return response
    } catch (error) {
      console.error('❌ Error updating timesheet:', {
        error: error.message,
        timesheetId
      })
      throw error
    }
  },

  /**
   * Get presigned URL for uploading receipt images
   * @param {string} filename - Original filename
   * @param {string} contentType - MIME type of the file
   * @param {number} technicianId - Technician ID
   * @param {string} weekStartDate - Week start date (YYYY-MM-DD)
   * @returns {Promise} API response with upload URL and S3 key
   */
  async getTimesheetUploadUrl(filename, contentType, technicianId, weekStartDate) {
    console.log('🔗 Getting upload URL:', {
      filename,
      contentType,
      technicianId,
      weekStartDate,
      timestamp: new Date().toISOString()
    })

    try {
      const response = await authenticatedApi.post('/timesheets/upload-url', {
        filename,
        content_type: contentType,
        technician_id: technicianId,
        week_start_date: weekStartDate
      })
      console.log('✅ Upload URL generated:', {
        s3Key: response?.s3_key,
        expiresIn: response?.expires_in
      })
      return response
    } catch (error) {
      console.error('❌ Error getting upload URL:', {
        error: error.message,
        filename
      })
      throw error
    }
  },

  /**
   * Get presigned URLs for viewing receipt images
   * @param {string[]} s3Keys - Array of S3 keys
   * @returns {Promise} API response with download URLs
   */
  async getReceiptUrls(s3Keys) {
    console.log('Getting receipt URLs:', { keyCount: s3Keys?.length })
    try {
      const response = await authenticatedApi.post('/timesheets/receipt-urls', {
        s3_keys: s3Keys
      })
      return response
    } catch (error) {
      console.error('Error getting receipt URLs:', error.message)
      throw error
    }
  },

  // ===== INSPECTION API METHODS =====

  async createInspection(payload) {
    return authenticatedApi.post('/inspections', payload)
  },

  async getInspection(inspectionId) {
    return authenticatedApi.get(`/inspections/${inspectionId}`)
  },

  async updateInspection(inspectionId, data) {
    return authenticatedApi.patch(`/inspections/${inspectionId}`, data)
  },

  async listInspections(technicianId, status = null, page = 1, limit = 20) {
    const params = new URLSearchParams({ technician_id: technicianId, page, limit })
    if (status) params.append('status', status)
    return authenticatedApi.get(`/inspections?${params.toString()}`)
  },

  async saveZoneFindings(inspectionId, zones) {
    return authenticatedApi.put(`/inspections/${inspectionId}/zones`, { zones })
  },

  async saveConduciveConditions(inspectionId, conditions) {
    return authenticatedApi.put(`/inspections/${inspectionId}/conditions`, { conditions })
  },

  async saveLimitations(inspectionId, limitations) {
    return authenticatedApi.put(`/inspections/${inspectionId}/limitations`, { limitations })
  },

  async saveRecommendations(inspectionId, recommendations) {
    return authenticatedApi.put(`/inspections/${inspectionId}/recommendations`, { recommendations })
  },

  async getInspectionUploadUrl(inspectionId, zoneKey, filename, contentType) {
    return authenticatedApi.post('/inspections/upload-url', {
      inspection_id: inspectionId,
      zone_key: zoneKey,
      filename,
      content_type: contentType
    })
  },

  async getInspectionPhotoUrls(s3Keys) {
    return authenticatedApi.post('/inspections/photo-urls', { s3_keys: s3Keys })
  },

  async generateInspectionReport(inspectionId) {
    return authenticatedApi.post(`/inspections/${inspectionId}/generate`, {})
  },

  async getInspectionReportUrl(inspectionId) {
    return authenticatedApi.get(`/inspections/${inspectionId}/report-url`)
  },

  // ===== COMMERCIAL REPORT API METHODS =====

  async generateCommercialReport(payload) {
    return authenticatedApi.post('/reports/generate-pdf', payload)
  },

  async listCommercialReports({ startDate = null, search = '', limit = 20, nextToken = null } = {}) {
    const params = new URLSearchParams({ limit })
    if (search) params.append('search', search)
    if (startDate) params.append('startDate', startDate)
    if (nextToken) params.append('nextToken', nextToken)
    return authenticatedApi.get(`/reports/list?${params.toString()}`)
  },

  async getCommercialReportUrl(s3Key) {
    return authenticatedApi.post('/reports/presigned-url', { s3Key })
  },

  // ===== CUSTOMER API METHODS =====

  async searchCustomers(search, limit = 8) {
    const params = new URLSearchParams({ search, limit })
    return authenticatedApi.get(`/customers?${params.toString()}`)
  }
  }
}

export { createApiServiceWithAuth }
export default api